import { BotProvider, useDeleteApiBotProviderActionsDeleteHistory, WebBotProviderData } from "@/API";
import { CopyText } from "@/Components/CopyText";
import { Button, Group, Text, Box, Tabs } from "@mantine/core";
import React, { useEffect, useState } from "react";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import { IconBug, IconTrash, IconX } from "@tabler/icons-react";
import { BOT_PROVIDER_METADATA_KEYS } from "./constants";
import { GetWebBotAvatarUrl, GetWebBotWidgetUrl } from "@/APIURL";

export function WebBotProviderDataCard(props: { botProvider: BotProvider }) {
    var botProviderData = props.botProvider.providerData as WebBotProviderData;

    let combinedHtml = "";
    let nodeJsExampleHtml = "";

    const isSecurityEnabled = botProviderData?.security?.enableSecurity;
    const secretKey = botProviderData?.security?.secretKey;

    if (isSecurityEnabled) {
        combinedHtml = `<script src="${window.origin}/api/WebBot/script"></script>\n<cakechat-widget bot-provider-id="${props.botProvider.id}" host="${window.origin}" user-token="您的JWT Token"></cakechat-widget>\n<!-- 請注意：此 Bot 已開啟安全驗證，需由您的伺服器使用 SecretKey 簽發 JWT Token 帶入 user-token 屬性中 -->`;

        nodeJsExampleHtml = `// 伺服器端 Node.js 產生 Token 範例
// 1. 請先安裝套件: npm install jsonwebtoken
const jwt = require('jsonwebtoken');

// 2. 這是您的專屬 SecretKey (請妥善保管，絕對不要寫在前端網頁中！)
const SECRET_KEY = "${secretKey || "您的 SecretKey"}";
const userId = "系統中實際的使用者ID"; 

// 3. 簽發 JWT Token，設定過期時間 (例如 1 小時)
const token = jwt.sign({ sub: userId }, SECRET_KEY, { expiresIn: '1h' });

// 4. 將此 token 傳給前端，並帶入 widget 屬性: <cakechat-widget user-token="\${token}">`;
    } else {
        combinedHtml = `<script src="${window.origin}/api/WebBot/script"></script>\n<cakechat-widget bot-provider-id="${props.botProvider.id}" host="${window.origin}" user-id="您的使用者ID(選填)">\n  <!-- 可選：自訂擴充區 Slot (例如 slot="extension-mascot"，將覆蓋後台預設模板) -->\n  <!-- <div slot="extension-mascot">...自訂內容...</div> -->\n</cakechat-widget>`;
    }

    const deleteHistory_Mutation = useDeleteApiBotProviderActionsDeleteHistory();
    const [isTesting, setIsTesting] = useState(false);
    const [widgetKey, setWidgetKey] = useState(0);

    useEffect(() => {
        setWidgetKey(prev => prev + 1);
    }, [JSON.stringify(botProviderData)]);

    useEffect(() => {
        if (isTesting) {
            const scriptId = 'web-bot-script';
            if (!document.getElementById(scriptId)) {
                const script = document.createElement('script');
                script.id = scriptId;
                script.src = `${window.origin}/api/WebBot/script`;
                script.async = true;
                document.body.appendChild(script);
            }
        }
    }, [isTesting]);

    const handleClearHistory = () => {
        modals.openContextModal({
            modal: "ConfirmCountDown",
            title: '清空測試紀錄',
            innerProps: {
                countDownSecond: 0,
                children: (
                    <Text size="sm">
                        確定要清空測試用的對話紀錄嗎？此動作無法復原。
                    </Text>
                ),
                labels: {
                    confirm: '清空', cancel: '取消'
                },
                confirmProps: { color: 'red' },
                onConfirm: async () => {
                    try {
                        await deleteHistory_Mutation.mutateAsync({
                            params: {
                                workspaceId: props.botProvider.workspaceId,
                                botProviderId: props.botProvider.id,
                                userId: "test-user-id"
                            }
                        });
                        notifications.show({ message: "已清空測試紀錄" });
                        setWidgetKey(prev => prev + 1);
                    } catch (error) {
                        notifications.show({ message: "清空測試紀錄失敗", color: "red" });
                        console.error(error);
                    }
                },
            },
        });
    };

    const buttonWidth = botProviderData?.layout?.buttonWidth ?? 48;
    const buttonHeight = botProviderData?.layout?.buttonHeight ?? 48;
    const buttonShape = botProviderData?.layout?.buttonShape?.toString().toLowerCase();

    return <>
        {props.botProvider.metadata?.[BOT_PROVIDER_METADATA_KEYS.avatarIconName] && (
            <>
                <Text>機器人頭像</Text>
                <div style={{ padding: '8px 0' }}>
                    <img
                        src={GetWebBotAvatarUrl(props.botProvider.id ?? "")}
                        alt="Bot Avatar Icon"
                        style={{ width: '48px', height: '48px', borderRadius: '50%', objectFit: 'cover' }}
                    />
                </div>
            </>
        )}
        {props.botProvider.metadata?.[BOT_PROVIDER_METADATA_KEYS.widgetIconName] && (
            <>
                <Text>聊天室開關圖示</Text>
                <div style={{ padding: '8px 0' }}>
                    <img
                        src={GetWebBotWidgetUrl(props.botProvider.id ?? "")}
                        alt="Bot Widget Icon"
                        style={{
                            width: `${buttonWidth}px`,
                            height: `${buttonHeight}px`,
                            maxWidth: '120px',
                            maxHeight: '120px',
                            borderRadius: buttonShape !== 'original' ? '50%' : '0px',
                            objectFit: buttonShape !== 'original' ? 'cover' : 'contain'
                        }}
                    />
                </div>
            </>
        )}
        <Text>標題</Text>
        <CopyText text={botProviderData?.text?.title ?? ""} />
        {botProviderData?.text?.botName && (
            <>
                <Text>Bot名稱</Text>
                <CopyText text={botProviderData.text.botName} />
            </>
        )}
        <Text>主題顏色</Text>
        <CopyText text={botProviderData?.theme?.themeColor ?? ""} />

        <Tabs defaultValue="widget" mt="md">
            <Tabs.List>
                <Tabs.Tab value="widget">官方預設 Widget 嵌入</Tabs.Tab>
                <Tabs.Tab value="sdk">自訂 UI (Headless SDK)</Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="widget" pt="xs">
                <Text mt="xs" fw={500}>HTML 嵌入代碼</Text>
                <CopyText text={combinedHtml} />

                {isSecurityEnabled && (
                    <Box mt="md">
                        <Text fw={500} c="dimmed">後端產生 Token 範例 (Node.js)</Text>
                        <CopyText text={nodeJsExampleHtml} />
                    </Box>
                )}
            </Tabs.Panel>

            <Tabs.Panel value="sdk" pt="xs">
                <Text size="sm" c="dimmed" mt="xs">
                    適合需要完全自訂對話框外觀、品牌排版或使用 React / Vue / Vanilla JS 的網站，不依賴任何官方 CSS。
                </Text>

                <Text mt="md" fw={500}>1. 引入 Headless SDK 腳本</Text>
                <CopyText text={`<script src="${window.origin}/api/WebBot/sdk"></script>`} />

                <Text mt="md" fw={500}>2. 下載 TypeScript 型別定義檔 (.d.ts)</Text>
                <CopyText text={`curl -o src/types/cakechat-sdk.d.ts ${window.origin}/api/WebBot/sdk.d.ts`} />
                <Group mt="xs">
                    <Button
                        component="a"
                        href={`${window.origin}/api/WebBot/sdk.d.ts`}
                        download="cakechat-sdk.d.ts"
                        variant="light"
                        size="xs"
                    >
                        直接下載 cakechat-sdk.d.ts
                    </Button>
                </Group>

                <Text mt="md" fw={500}>3. React + TypeScript 入門範例</Text>
                <CopyText text={`import React from 'react';

// 建立綁定當前專案的 React Hooks
const { useCakeChat, useBotConfig, CakeChatProvider } = window.CakeChat.createReactHooks(React);

export function CustomChat() {
  // 取得後台風格設定與圖片 URL（按需載入）
  const { config, avatarUrl, isConfigLoaded } = useBotConfig("${props.botProvider.id}", "${window.origin}");

  // 預設 autoConnect: true 自動連線與同步對話歷史；ID 未就緒時提供 Safe Idle 不閃退
  const { messages, input, setInput, sendMessage, isProcessing, botState, isReady } = useCakeChat({
    botProviderId: "${props.botProvider.id}",
    host: "${window.origin}",${isSecurityEnabled ? `\n    userToken: "您的JWT Token",` : ""}
    autoConnect: true,
  });

  return (
    <div className="my-chat">
      <div className="header">
        {avatarUrl && <img src={avatarUrl} alt="Avatar" width={32} height={32} />}
        <span>{config.text.botName || config.text.title}</span>
        <span className="status">狀態: {botState}</span>
      </div>
      <div className="messages">
        {messages.map((msg, i) => (
          <div key={i} className={\`msg-\${msg.role}\`}>
            <strong>{msg.role === 'user' ? '您' : 'AI'}: </strong>
            {msg.message}
          </div>
        ))}
      </div>
      <form onSubmit={e => { e.preventDefault(); sendMessage(); }}>
        <input value={input} onChange={e => setInput(e.target.value)} placeholder="請輸入訊息..." />
        <button type="submit" disabled={isProcessing || !isReady}>送出</button>
      </form>
    </div>
  );
}

// 亦可使用 CakeChatProvider 全站共享單一連線（子組件免傳參）：
// <CakeChatProvider client={new window.CakeChat.CakeChatClient({ botProviderId: "${props.botProvider.id}" })}>
//   <ChildChatView /> {/* 內部直接呼叫: const { messages, sendMessage } = useCakeChat(); */}
// </CakeChatProvider>`} />

                <Text mt="md" fw={500}>4. Vanilla JS / 純 HTML 範例</Text>
                <CopyText text={`<script src="${window.origin}/api/WebBot/sdk"></script>
<script>
  const client = new window.CakeChat.CakeChatClient({
    botProviderId: "${props.botProvider.id}",
    host: "${window.origin}",${isSecurityEnabled ? `\n    userToken: "您的JWT Token",` : ""}
    autoConnect: true,
  });

  // 取得後台外觀設定與圖片 URL
  client.fetchConfig().then((config) => {
    console.log('機器人名稱:', config.text.botName);
    console.log('頭像網址:', client.getAvatarUrl());
  });

  // 訂閱全域狀態變更
  client.subscribe((state) => {
    console.log('連線狀態:', state.connectionStatus);
    console.log('最新訊息列表:', state.messages);
  });

  // 監聽單一訊息發送與接收事件（內容支援 e.message / e.text，role 統一為小寫 'user' / 'assistant'）
  client.on('messageReceived', (e) => {
    console.log(\`[\${e.role}]收到新訊息:\`, e.text, '完成狀態:', e.isComplete);
  });

  // 發送訊息: client.sendMessage('你好！');
</script>`} />
            </Tabs.Panel>
        </Tabs>

        <Group mt="md" align="center">
            <Button
                onClick={() => setIsTesting(!isTesting)}
                color={isTesting ? "red" : "blue"}
                leftSection={isTesting ? <IconX size={18} /> : <IconBug size={18} />}
            >
                {isTesting ? "關閉測試" : "測試 (開發者身分)"}
            </Button>
            {isTesting && (
                <Button
                    onClick={handleClearHistory}
                    variant="light"
                    color="red"
                    leftSection={<IconTrash size={18} />}
                >
                    清空測試紀錄
                </Button>
            )}
        </Group>

        {isTesting && React.createElement("cakechat-widget", {
            key: widgetKey,
            "bot-provider-id": props.botProvider.id,
            "host": window.origin,
            "user-id": "test-user-id"
        })}
    </>;
}
